import { marked } from "marked";
import {
  enhanceRenderedContentLinks,
  linkifyContentMarkdownSource,
} from "./contentLinkUtils";

/** Decode entities for text extracted from HTML (browser + simple Node fallback). */
function decodeHtmlEntities(str) {
  if (!str) return str;
  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = str;
    return textarea.value;
  }
  return str
    .replace(/&nbsp;/gi, " ")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) =>
      String.fromCharCode(parseInt(h, 16)),
    )
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"');
}

/**
 * When the API returns HTML that still contains markdown (e.g. `<p>**Name**</p>`),
 * marked leaves `**` as literal. Strip to plain text (preserving line breaks) so
 * markdown can run. HTML that only uses `<strong>` is unchanged until we normalize
 * those tags to `**…**` first.
 */
function htmlEmailBodyToMarkdownSource(html) {
  let s = html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "");
  s = s
    .replace(/<(strong|b)\b[^>]*>([\s\S]*?)<\/\1>/gi, "**$2**")
    .replace(/<(em|i)\b[^>]*>([\s\S]*?)<\/\1>/gi, "*$2*");
  s = s
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|h[1-6]|blockquote|tr)>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<[^>]+>/g, "");
  return decodeHtmlEntities(s).trim();
}

function looksLikeMarkdownInsideHtml(text) {
  return (
    /\*\*[\s\S]*?\*\*/.test(text) ||
    /__(?=\S)[\s\S]*?\S__/.test(text)
  );
}

/**
 * Sales emails often end with "Best,\nName\nTitle" without **markdown**. Lines after
 * a paragraph break + "Best," are wrapped in **…** so they render bold (skips lines
 * already bold or bullets). Skips if the tail looks like HTML.
 */
function applyClosingSignatureBoldAfterBest(text) {
  if (!text || typeof text !== "string") return text;
  const re = /((?:\r?\n){2,}Best,\s*\r?\n)([\s\S]*)$/i;
  const m = text.match(re);
  if (!m) return text;
  const sigBody = m[2];
  if (sigBody.includes("<")) return text;

  const head = text.slice(0, m.index) + m[1];
  const lines = sigBody.split(/\r?\n/);
  const out = lines.map((line) => {
    const trimmed = line.trim();
    if (!trimmed) return line;
    if (/^\*\*.+\*\*$/.test(trimmed)) return line;
    if (/^[*\-•]\s/.test(trimmed)) return line;
    if (/^\d+\.\s/.test(trimmed)) return line;
    const lead = line.match(/^\s*/)[0];
    return `${lead}**${trimmed}**`;
  });
  return head + out.join("\n");
}

/** Map fullwidth / confusable asterisks and underscores so **bold** / __bold__ parse. */
function normalizeMarkdownDelimiters(text) {
  if (!text || typeof text !== "string") return text;
  return text
    .replace(/\uFF0A/g, "*")
    .replace(/\u2217/g, "*")
    .replace(/\u204E/g, "*")
    .replace(/\uFF3F/g, "_");
}

export const normalizeMarkdownNewlines = (text) => {
  if (!text || typeof text !== "string") return text;
  return text
    .replace(/\\n/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\n?\\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/\n\s*$/, "");
};

export const markdownToHtml = (markdownText, options = {}) => {
  if (!markdownText || typeof markdownText !== "string") {
    return "";
  }

  const processedText = normalizeMarkdownNewlines(
    normalizeMarkdownDelimiters(markdownText),
  );

  const defaultOptions = {
    breaks: true,
    gfm: true,
    ...options,
  };

  try {
    return marked.parse(processedText, defaultOptions);
  } catch (error) {
    console.error("Error parsing markdown:", error);
    return markdownText;
  }
};

/** Markdown structures that own their line (list, heading, quote, table, fence). */
const STRUCTURE_LINE = /^\s*(?:[-*+•]\s|\d+[.)]\s|#{1,6}\s|>|\||```|~~~)/;

/** Block-level HTML means the markup already carries the spacing. */
const BLOCK_HTML = /<\/(?:p|div|h[1-6]|blockquote|tr|table|ul|ol|li)>|<br\s*\/?>/i;

/** A line ending mid-sentence: no terminal punctuation, or a dangling connector. */
const INCOMPLETE_LINE_END = /(?:[^.!?…]["')\]]?|[&+\-–—/,:;])\s*$/;

/** A line left dangling on a connector continues, whatever the next line's case. */
const DANGLING_CONNECTOR_END = /[&+/\u2013\u2014-]\s*$/;

/** A line that continues the previous one rather than starting a new thought. */
const CONTINUATION_LINE_START = /^["'(\[]?[a-z0-9]/;

/**
 * A hard-wrapped line runs close to the wrap column. Shorter lines (a name, a title,
 * a company) were broken on purpose, so only a line at least this long can have
 * been wrapped mid-sentence.
 */
const MIN_WRAPPED_LINE_LENGTH = 40;

/** A line that is one contact detail on its own: email, URL or phone number. It is never
 *  a sentence fragment, so it is neither joined to the line above nor absorbs the next. */
const CONTACT_ATOM_LINE =
  /^(?:[\w.+'-]+@[\w-]+(?:\.[\w-]+)+|(?:https?:\/\/|www\.)\S+|[a-z0-9-]+(?:\.[a-z0-9-]+)+\/?\S*|\+?[\d][\d\s().-]{5,})$/i;

/** Candidate sentence boundary: terminal punctuation, then a capitalised word. */
const SENTENCE_BOUNDARY = /(\S[.!?]["')\]]?)(\s+)(?=["'(\[]?[A-Z])/g;

/** Dotted abbreviation ("U.S.", "e.g.", "Ph.D.") - a stop there is not a sentence end. */
const DOTTED_ABBREVIATION = /(?:^|[\s("'[])(?:[A-Za-z]\.){2,}$/;

/** Greeting ("Hello Glen," / "Hi team -") at the very start of a one-line email:
 *  a short opener up to its FIRST comma, and only when a new sentence (capital)
 *  follows - so it never swallows part of a sentence, and a clause that merely
 *  continues after the comma stays in one paragraph. */
const LEADING_GREETING = /^([^.!?,:\n]{1,40}[,:–—-])\s+(?=["'(\[]?[A-Z])/;

/**
 * Rebuild one blank-line-separated block: fold lines that are visibly wrapped
 * mid-sentence back together, and keep every other newline as a hard break
 * (marked runs with breaks:true, so they render as <br>). Structural only - no
 * word, title or closing-phrase lists, so prose containing "best", "regards" or
 * "thanks" can never be mistaken for the start of a signature.
 */
function unwrapBlockLines(block) {
  const lines = block.split("\n");
  const out = [];

  for (const line of lines) {
    const prev = out.length ? out[out.length - 1] : null;
    const canJoin =
      prev !== null &&
      prev.trim() !== "" &&
      line.trim() !== "" &&
      !STRUCTURE_LINE.test(prev) &&
      !STRUCTURE_LINE.test(line) &&
      !CONTACT_ATOM_LINE.test(prev.trim()) &&
      !CONTACT_ATOM_LINE.test(line.trim()) &&
      INCOMPLETE_LINE_END.test(prev) &&
      ((CONTINUATION_LINE_START.test(line.trim()) &&
        prev.trim().length >= MIN_WRAPPED_LINE_LENGTH) ||
        DANGLING_CONNECTOR_END.test(prev));

    if (canJoin) {
      out[out.length - 1] = prev.replace(/\s+$/, "") + " " + line.trim();
    } else {
      out.push(line);
    }
  }

  return out.join("\n");
}

/** One-line email: derive paragraphs from sentence structure, not from phrases. */
function paragraphsFromSingleLine(text) {
  let rest = text;
  let greeting = "";

  const greetingMatch = rest.match(LEADING_GREETING);
  if (greetingMatch) {
    greeting = greetingMatch[1].trim();
    rest = rest.slice(greetingMatch[0].length);
  }

  const body = rest
    .replace(SENTENCE_BOUNDARY, (match, ending, gap, offset) => {
      const upTo = rest.slice(0, offset) + ending;
      return DOTTED_ABBREVIATION.test(upTo) ? match : ending + "\n\n";
    })
    .trim();
  return [greeting, body].filter(Boolean).join("\n\n");
}

/**
 * Normalise an email body into markdown whose paragraph structure survives rendering.
 *
 * Content that already separates paragraphs with blank lines (what the generator
 * produces) keeps that structure exactly: the only rewriting is re-joining lines
 * that were hard-wrapped mid-sentence.
 */
export const formatEmailSpacing = (text) => {
  if (!text || typeof text !== "string") return text;

  const normalized = normalizeMarkdownNewlines(text).trim();
  if (!normalized) return normalized;

  // HTML already encodes its own spacing; leave it for the HTML path.
  if (BLOCK_HTML.test(normalized)) return normalized;

  const blocks = normalized.split(/\n{2,}/).map(unwrapBlockLines);

  // A whole email on one line has no structure to preserve, so derive it.
  if (blocks.length === 1 && !blocks[0].includes("\n")) {
    return paragraphsFromSingleLine(blocks[0]);
  }

  return blocks.join("\n\n");
};

/** Email  markdown + HTML-from-API; sanitize at call site. */
export const renderEmailContent = (text) => {
  if (!text) return "";
  const formatted = formatEmailSpacing(text);
  const hasTag = /<[a-zA-Z!/][^>]*>/.test(formatted);
  let source =
    hasTag && looksLikeMarkdownInsideHtml(formatted)
      ? htmlEmailBodyToMarkdownSource(formatted)
      : formatted;
  source = applyClosingSignatureBoldAfterBest(source);
  source = linkifyContentMarkdownSource(source);
  return enhanceRenderedContentLinks(markdownToHtml(source));
};

export const getMarkdownProps = (markdownText, className = "", style = {}) => {
  return {
    className: `text-[0.792rem] text-black font-normal max-w-none markdown-content ${className}`,
    style: {
      wordBreak: "break-word",
      ...style,
    },
    dangerouslySetInnerHTML: {
      __html: enhanceRenderedContentLinks(
        markdownToHtml(linkifyContentMarkdownSource(markdownText)),
      ),
    },
  };
};


